import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@/api/client';
import { authApi } from '@/api/endpoints';
import { setToken } from '@/api/token-store';
import type { LoginInput, RegisterInput, RegisterResponse, User } from '@/api/types';

interface AuthContextValue {
  user: User | null;
  /** true, solange der initiale /auth/me-Check läuft */
  initializing: boolean;
  login: (input: LoginInput) => Promise<void>;
  /**
   * §11.2: KEIN Auto-Login. Legt nur einen pending-Account an und liefert
   * `{ user, pending }` zurück, damit die UI den Freischaltungs-Hinweis zeigt.
   */
  register: (input: RegisterInput) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  /** Aktualisiert den lokalen User-State nach einer erfolgreichen Profil-Änderung. */
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Beim Start: aktuellen User über /auth/me ermitteln (Cookie- oder Token-Session).
  useEffect(() => {
    const controller = new AbortController();
    authApi
      .me(controller.signal)
      .then((me) => setUser(me))
      .catch((err: unknown) => {
        // 401 ist hier normal (nicht eingeloggt) — Token verwerfen.
        if (err instanceof ApiError && err.status === 401) setToken(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setInitializing(false);
      });
    return () => controller.abort();
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const res = await authApi.login(input);
    if (res.token) setToken(res.token);
    setUser(res.user);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    // Kein Auto-Login: Account ist pending und braucht Admin-Freischaltung.
    return authApi.register(input);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Logout serverseitig fehlgeschlagen — lokal trotzdem abmelden.
    }
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((next: User) => setUser(next), []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, initializing, login, register, logout, updateUser }),
    [user, initializing, login, register, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden.');
  return ctx;
}
